import { useQuery } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { isDomainReady } from "~/components/siteSpeed/helpers";

import { Domain } from "../../../api/Api";
import { apiClient } from "../../../api/ApiClient";
import { Icon } from "../../../assets/icons/Icon";
import { useWizard } from "../../../contexts/WizardContext";
import FormLabel from "../../login/formLabel/FormLabel";
import Button from "../../mantine/button/Button";
import Input from "../../ui/input/Input";
import Spinner from "../../ui/spinner/Spinner";
import { ns } from "../helpers";
import styles from "./Wizzard.module.scss";

type NameserversProps = {
  isWizzard?: boolean;
  domain: Domain;
};

const Nameservers = ({ isWizzard = false, domain }: NameserversProps) => {
  const { domainId } = useParams<{ domainId: string }>();
  const { navigateToPrevStep, updateDomain } = useWizard();
  const [isPolling, setIsPolling] = useState(true);
  const [isPollingDomain, setIsPollingDomain] = useState(true);
  const [copiedFields, setCopiedFields] = useState<{ [key: string]: boolean }>(
    {},
  );
  const isReady = isDomainReady(domain);

  useEffect(() => {
    if (!isPolling && !isReady)
      enqueueSnackbar("Domain configuration complete!", { variant: "success" });
  }, [isPolling, isReady]);

  // Here we need to use the special statusDetail to force the
  // status resync at the backend.
  const { data: domainStatus } = useQuery({
    queryKey: ["domainStatus", domainId],
    queryFn: async () => {
      setIsPollingDomain(true);
      const response = await apiClient.domains.statusDetail(Number(domainId));
      setIsPollingDomain(false);

      return response?.data?.domain.status || {};
    },

    refetchOnWindowFocus: false,
    refetchInterval: isPolling ? 10000 : false,
    refetchOnMount: true,
  });

  const { data: nsHostsStateDetail } = useQuery({
    enabled: !isPollingDomain,
    queryKey: ["nsHostsStateDetail", domainId],
    queryFn: async () => {
      const response = await apiClient.domains.nsHostsStateDetail(
        Number(domainId),
      );

      setIsPolling(!domainStatus?.ns || !response?.data?.ns_hosts_state);

      return response?.data?.ns_hosts_state || {};
    },
    refetchOnWindowFocus: false,
    refetchInterval: isPolling ? 10000 : false,
    refetchOnMount: true,
  });

  const handleCopy = (text: string, index: number) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopiedFields((prev) => ({
          ...prev,
          [`type-${index}`]: true,
        }));

        setTimeout(() => {
          setCopiedFields((prev) => ({
            ...prev,
            [`type-${index}`]: false,
          }));
        }, 2000);
      })
      .catch((err) => {
        console.error("Failed to copy text: ", err);
      });
  };

  const handleContinue = async () => {
    if (isWizzard) {
      // Update Domain state
      const result = await apiClient.domains.domainsDetail(Number(domainId));
      const updatedDomain = result?.data?.domain;

      if (updatedDomain) updateDomain(updatedDomain);
    }
  };

  const handleBack = () => {
    if (isWizzard) {
      navigateToPrevStep();
    }
  };

  const pollingStatus = isPolling && (
    <Spinner size="1rem" className={styles.pollingStatus} />
  );

  return (
    // @ts-expect-error TS2339 non-existing className
    <div className={styles.stepContent}>
      <div className={styles.stepContentTitle}>Nameservers</div>
      <div className={styles.stepContentWrapper}>
        {/*
         // @ts-expect-error TS2551 non-existing className */}
        <div className={styles.stepContentSection}>
          <ol className={styles.stepContentList}>
            <li>
              Go to your domain registrar, log into your account and find DNS
              management.
            </li>
            <li>
              Set all nameservers with the values below. If your registrar shows
              less than four nameservers then set as many as possible, if more
              than four then leave the rest blank.
            </li>
          </ol>
          <div className={styles.stepContentListText}>
            {ns?.map((item, index) => {
              return (
                <div className={styles.stepContentCodeFlex} key={index}>
                  {/*
                   // @ts-expect-error TS2551 non-existing className */}
                  <div className={styles.stepContentCode}>
                    <FormLabel label={item.title} />
                    <div className={styles.stepContentCodeInput}>
                      <Input
                        readOnly
                        value={item.value}
                        ownProps={{ noErrors: true }}
                      />
                      <Button
                        variant="outlined"
                        onClick={() => handleCopy(item.value, index)}
                      >
                        {/*
                         // @ts-expect-error TS2339 non-existing className */}
                        <span className={styles.buttonText}>
                          <Icon
                            iconName={
                              copiedFields[`type-${index}`]
                                ? "CheckmarkIcon"
                                : "CopyIcon"
                            }
                          />
                          {copiedFields[`type-${index}`] ? "Copied!" : "Copy"}
                        </span>
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        {/*
         // @ts-expect-error TS2339 non-existing className */}
        <div className={styles.stepRightSection}>
          {/*
           // @ts-expect-error TS2339 non-existing className */}
          <div className={styles.sectionTitleContainer}>
            <div className={styles.sectionTitle}>
              <div className={styles.certificateTitle}>
                {pollingStatus} AI Visibility Engine Status
              </div>
            </div>
          </div>

          {nsHostsStateDetail &&
            Object.entries(nsHostsStateDetail).map(([domain, nameservers]) => (
              // @ts-expect-error TS2339 non-existing className
              <div key={domain} className={styles.statusContainer}>
                <div className={styles.domainHeader}>{domain}</div>
                <ul className={styles.statusList}>
                  {nameservers && typeof nameservers === "object"
                    ? Object.entries(nameservers).map(
                        ([nameserver, status]) => (
                          <li key={nameserver} className={styles.statusItem}>
                            {status === true ? (
                              <Icon
                                iconName="GreenCheckmarkIcon"
                                // @ts-expect-error TS2339 non-existing className
                                className={styles.statusIconActive}
                              />
                            ) : status === false ? (
                              <Icon
                                iconName="GreyCrossmarkIcon"
                                // @ts-expect-error TS2339 non-existing className
                                className={styles.statusIconInactive}
                              />
                            ) : (
                              <Icon
                                iconName="RedCrossmarkIcon"
                                // @ts-expect-error TS2339 non-existing className
                                className={styles.statusIconUnknown}
                              />
                            )}
                            {/*
                             // @ts-expect-error TS2339 non-existing className */}
                            <span className={styles.nameserverText}>
                              {nameserver}
                            </span>
                          </li>
                        ),
                      )
                    : null}
                </ul>
              </div>
            ))}
        </div>
      </div>
      {isWizzard && (
        <div className={styles.stepContentFooter}>
          {isWizzard && (
            <Button variant="outlined" onClick={handleBack}>
              Back
            </Button>
          )}
          <Button onClick={handleContinue} disabled={isPolling}>
            {isPolling && <Spinner size="1rem" />} Finish
          </Button>
        </div>
      )}
    </div>
  );
};

export default Nameservers;
